import { isEmpty, omit } from 'lodash';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import { ISavedFilter } from '../../../components/sidebar-filters/saved-filter/interface';
import { DEFAULT_DATE_FORMAT, getUtcFormattedTime } from '../../../domain/date/date';
import { ISidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { parseClearPeriodValue, parseDateRangeValue } from '../../settlements/view/vendorBillItems/sidebar/utils';
import { IEventCompanyParticipationPageState, IEventCompanyParticipationSidebarFilters } from './EventCompanyParticipationSidebarFiltersContext';
import { EventCompanyParticipationFilterKeys, TEventCompanyParticipationFilterKey } from './EventCompanyParticipationSidebarFilters';

export const transformEventCompanyParticipationPageStateToFilters = (
    pageState: IEventCompanyParticipationPageState
): IEventCompanyParticipationSidebarFilters => {
    const result: IEventCompanyParticipationSidebarFilters = {};
    const filterData = (pageState.filter || {}) as IEventCompanyParticipationSidebarFilters;

    // Convert period
    if (filterData.period !== undefined) {
        result.period = filterData.period;
    }

    // Convert event programs
    if (filterData.event_programs !== undefined) {
        result.event_programs = Array.isArray(filterData.event_programs) ? filterData.event_programs : [filterData.event_programs];
        result.event_programs = result.event_programs.map(val => String(val));
    }

    // Convert participation_statuses
    if (filterData.participation_statuses !== undefined) {
        result.participation_statuses = Array.isArray(filterData.participation_statuses)
            ? filterData.participation_statuses
            : [filterData.participation_statuses];
        result.participation_statuses = result.participation_statuses.map(val => String(val));
    }

    return result;
};

export const transformEventCompanyParticipationSavedFilterValuesFromDto = (
    filter: ISavedFilter['filter_values']
): Record<TEventCompanyParticipationFilterKey, unknown> => {
    const result: Record<TEventCompanyParticipationFilterKey, unknown> = {} as Record<TEventCompanyParticipationFilterKey, unknown>;

    for (const [key, value] of Object.entries(filter)) {
        if (isUndefined(value)) continue;

        switch (key) {
            case EventCompanyParticipationFilterKeys.period:
            case EventCompanyParticipationFilterKeys.event_programs:
            case EventCompanyParticipationFilterKeys.participation_statuses:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = value; // Keep other values as is
        }
    }

    return result;
};

export const transformEventCompanyParticipationSavedFilterValuesToDto = (
    filter: ISavedFilter['filter_values']
): Record<TEventCompanyParticipationFilterKey, unknown> => {
    const result: Record<TEventCompanyParticipationFilterKey, unknown> = {} as Record<TEventCompanyParticipationFilterKey, unknown>;

    for (const [key, value] of Object.entries(filter)) {
        switch (key) {
            case EventCompanyParticipationFilterKeys.period:
            case EventCompanyParticipationFilterKeys.event_programs:
            case EventCompanyParticipationFilterKeys.participation_statuses:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    return result;
};

export const transformEventCompanyParticipationSavedFilterToUser = (filter: ISavedFilter['filter_values'], context: ISidebarFilterOptionsContext) => {
    let result: Record<TEventCompanyParticipationFilterKey, string> = {} as Record<TEventCompanyParticipationFilterKey, string>;

    for (const [key, value] of Object.entries(filter)) {
        // Filter out deselected values to avoid them "unknown"
        if (isUndefined(value) || value === null) continue;

        switch (key) {
            case EventCompanyParticipationFilterKeys.period:
                // for custom range value we use such structure 'key::period'
                const clearPeriodValue = parseClearPeriodValue(value);

                const period =
                    context.eventCompanyParticipationPeriodOptions.find(option => {
                        return option.value === clearPeriodValue;
                    })?.label || 'unknown';

                if (clearPeriodValue !== 'custom_range') {
                    result[key] = period;
                    break;
                }

                const dateRange = parseDateRangeValue(value);

                if (!dateRange) {
                    result[key] = 'unknown';
                    break;
                }

                result[key] =
                    `${period}(${getUtcFormattedTime(dateRange.start, DEFAULT_DATE_FORMAT)}-${getUtcFormattedTime(dateRange.end, DEFAULT_DATE_FORMAT)})`;
                break;

            case EventCompanyParticipationFilterKeys.event_programs:
                result[key] = value
                    .map((val: string) => {
                        const option = context.alertTrackingProgramOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;

            case EventCompanyParticipationFilterKeys.participation_statuses:
                result[key] = value
                    .map((val: string) => {
                        const option = context.alertTrackParticipationStatusOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    // Remove empty properties from the result object
    const emptyProperties = getEmptyFilterProperties(result);
    if (emptyProperties.length > 0) {
        result = omit(result, emptyProperties) as Record<TEventCompanyParticipationFilterKey, string>;
    }

    return result;
};

export const checkFilterInvalidValues = (filter: ISavedFilter['filter_values'], context: ISidebarFilterOptionsContext) => {
    const invalidFields: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        if (isNil(value) || isEmpty(value)) continue;

        switch (key) {
            case EventCompanyParticipationFilterKeys.period:
                if (
                    typeof value === 'string' &&
                    !context.eventCompanyParticipationPeriodOptions.some(opt => {
                        // for custom range value we use such structure 'key::period'
                        const clearPeriod = value?.split('::')?.at(0);

                        return opt.value === clearPeriod;
                    })
                ) {
                    invalidFields.push(key);
                }
                break;

            case EventCompanyParticipationFilterKeys.event_programs:
                if (Array.isArray(value)) {
                    const validOptionValues = context.alertTrackingProgramOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            case EventCompanyParticipationFilterKeys.participation_statuses:
                if (Array.isArray(value)) {
                    const validOptionValues = context.alertTrackParticipationStatusOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            default:
                break; // Ignore other keys
        }
    }

    return invalidFields;
};

function getEmptyFilterProperties(obj: Record<TEventCompanyParticipationFilterKey, string>): TEventCompanyParticipationFilterKey[] {
    return Object.entries(obj)
        .filter(([_, value]) => isEmpty(value))
        .map(([key, _]) => key) as TEventCompanyParticipationFilterKey[];
}
